import type {
  ContractorInput,
  ContractorReport,
  ContractorProfile,
  ContractorSettings,
} from '@/types';
import { ensureCSRFToken, fetchAPI } from './backendClient';

/**
 * Submit contractor calculation (anonymous - no login required).
 * Uses POST /v1/calculator/contractor/
 */
export async function submitContractorAnonymous(
  payload: ContractorInput
): Promise<ContractorReport> {
  // Ensure CSRF token for anonymous POST
  await ensureCSRFToken();

  const resp = await fetchAPI('/v1/calculator/contractor/', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  if (!resp.ok) {
    const errorData = await resp.json().catch(() => ({}));
    // Extract first validation error if available
    const firstError = Object.values(errorData)[0];
    const message = Array.isArray(firstError)
      ? firstError[0]
      : errorData?.detail || `HTTP ${resp.status}`;
    throw new Error(message);
  }

  return resp.json();
}

/**
 * Submit contractor calculation (authenticated - uses saved settings).
 * Uses POST /v1/calculator/contractor/user/
 * Merges saved profile + tax year settings with request body overrides.
 */
export async function submitContractorAuthenticated(
  payload: Partial<ContractorInput>
): Promise<ContractorReport> {
  const resp = await fetchAPI('/v1/calculator/contractor/user/', {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  if (!resp.ok) {
    const errorData = await resp.json().catch(() => ({}));
    const firstError = Object.values(errorData)[0];
    const message = Array.isArray(firstError)
      ? firstError[0]
      : errorData?.detail || `HTTP ${resp.status}`;
    throw new Error(message);
  }

  return resp.json();
}

/**
 * Get contractor profile (structural setup).
 * Uses GET /v1/settings/contractor/profile/
 * Creates default profile if doesn't exist.
 */
export async function getContractorProfile(): Promise<ContractorProfile> {
  const resp = await fetchAPI('/v1/settings/contractor/profile/', {
    method: 'GET',
  });

  if (!resp.ok) {
    throw new Error(`Failed to load contractor profile: HTTP ${resp.status}`);
  }

  return resp.json();
}

/**
 * Save contractor profile (structural setup).
 * Uses PUT /v1/settings/contractor/profile/
 */
export async function saveContractorProfile(
  data: Partial<ContractorProfile>
): Promise<ContractorProfile> {
  const resp = await fetchAPI('/v1/settings/contractor/profile/', {
    method: 'PUT',
    body: JSON.stringify(data),
  });

  if (!resp.ok) {
    const errorData = await resp.json().catch(() => ({}));
    const firstError = Object.values(errorData)[0];
    const message = Array.isArray(firstError)
      ? firstError[0]
      : `Failed to save contractor profile: HTTP ${resp.status}`;
    throw new Error(message);
  }

  return resp.json();
}

/**
 * Get contractor expected values (from current tax year).
 * Uses GET /v1/settings/contractor/
 */
export async function getContractorSettings(): Promise<ContractorSettings> {
  const resp = await fetchAPI('/v1/settings/contractor/', {
    method: 'GET',
  });

  if (!resp.ok) {
    throw new Error(`Failed to load contractor settings: HTTP ${resp.status}`);
  }

  return resp.json();
}

/**
 * Save contractor expected values (to current tax year).
 * Uses PUT /v1/settings/contractor/
 */
export async function saveContractorSettings(
  data: Partial<ContractorSettings>
): Promise<ContractorSettings> {
  const resp = await fetchAPI('/v1/settings/contractor/', {
    method: 'PUT',
    body: JSON.stringify(data),
  });

  if (!resp.ok) {
    const errorData = await resp.json().catch(() => ({}));
    const firstError = Object.values(errorData)[0];
    const message = Array.isArray(firstError)
      ? firstError[0]
      : `Failed to save contractor settings: HTTP ${resp.status}`;
    throw new Error(message);
  }

  return resp.json();
}
